import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Loader, Message, Panel, Placeholder, Table, Tag } from "rsuite";
import AdminDashboard from "../../components/AdminDashboard";
import { DriverStatus } from "../../my-enum";
import { Driver } from "../../my-type";
import { authUserData } from "../../service";
import { approveLeadApply, getDriversByTeamCode } from "../../service/DriverService";
import { isTeamAdminUser } from "../../util/teamAdmin";

export default function LeadApplicationList() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingUserId, setApprovingUserId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const authUser = authUserData();
  const isTeamAdmin = isTeamAdminUser(authUser);

  const fetchDrivers = async () => {
    if (!authUser?.team?.code) {
      setDrivers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const fetchedDrivers = await getDriversByTeamCode(authUser.team.code);
      setDrivers(fetchedDrivers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "載入代理隊長申請失敗");
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authUser) {
      navigate("/login");
      return;
    }

    void fetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.team?.code, navigate]);

  const handleApprove = async (driver: Driver) => {
    if (!isTeamAdmin || approvingUserId !== null) {
      return;
    }

    setApprovingUserId(driver.userUID);
    setError("");
    setSuccessMessage("");

    try {
      const success = await approveLeadApply(driver.userUID);
      if (!success) {
        throw new Error("核准代理隊長申請失敗");
      }

      setDrivers((prevDrivers) =>
        prevDrivers.map((existingDriver) =>
          existingDriver.userUID === driver.userUID
            ? {
                ...existingDriver,
                leadApplicationStatus: 1,
                leadApplicationApprovedAt: new Date().toISOString(),
              }
            : existingDriver
        )
      );
      setSuccessMessage(`${driver.driverName} 的代理隊長申請已核准`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "核准代理隊長申請時發生錯誤");
    } finally {
      setApprovingUserId(null);
    }
  };

  const pendingApplications = drivers.filter((driver) => driver.leadApplicationStatus === 0);

  return (
    <AdminDashboard title="代理隊長申請">
      {!isTeamAdmin && (
        <Message type="warning" style={{ marginBottom: 20 }}>
          只有車隊管理員可以核准代理隊長申請。
        </Message>
      )}

      {error && (
        <Message type="error" style={{ marginBottom: 20 }}>
          {error}
        </Message>
      )}

      {successMessage && (
        <Message type="success" style={{ marginBottom: 20 }}>
          {successMessage}
        </Message>
      )}

      <Panel header={`代理隊長申請 (${pendingApplications.length} 筆)`} bordered className="fleet-review-panel">
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Loader size="lg" content="載入中..." />
          </div>
        ) : pendingApplications.length > 0 ? (
          <Table
            data={pendingApplications}
            autoHeight
            hover
            bordered
            cellBordered
            rowHeight={60}
          >
            <Table.Column width={150} fixed>
              <Table.HeaderCell>司機姓名</Table.HeaderCell>
              <Table.Cell dataKey="driverName" />
            </Table.Column>
            <Table.Column width={120}>
              <Table.HeaderCell>用戶 ID</Table.HeaderCell>
              <Table.Cell dataKey="userUID" />
            </Table.Column>
            <Table.Column width={150}>
              <Table.HeaderCell>電話</Table.HeaderCell>
              <Table.Cell dataKey="phoneNumber" />
            </Table.Column>
            <Table.Column width={150}>
              <Table.HeaderCell>車號</Table.HeaderCell>
              <Table.Cell dataKey="licencePlateNumber" />
            </Table.Column>
            <Table.Column width={150}>
              <Table.HeaderCell>司機狀態</Table.HeaderCell>
              <Table.Cell>
                {(rowData: Driver) => (
                  <Tag color={rowData.status === DriverStatus.已驗證 ? "green" : "orange"}>
                    {DriverStatus[rowData.status]}
                  </Tag>
                )}
              </Table.Cell>
            </Table.Column>
            <Table.Column width={200}>
              <Table.HeaderCell>申請時間</Table.HeaderCell>
              <Table.Cell>
                {(rowData: Driver) =>
                  rowData.leadApplicationAppliedAt
                    ? new Date(rowData.leadApplicationAppliedAt).toLocaleString("zh-TW")
                    : "-"
                }
              </Table.Cell>
            </Table.Column>
            <Table.Column width={220} fixed="right">
              <Table.HeaderCell>操作</Table.HeaderCell>
              <Table.Cell>
                {(rowData: Driver) => (
                  <>
                    <Button
                      appearance="primary"
                      color="green"
                      size="sm"
                      loading={approvingUserId === rowData.userUID}
                      disabled={!isTeamAdmin || approvingUserId !== null}
                      onClick={() => handleApprove(rowData)}
                      style={{ marginRight: 8 }}
                    >
                      核准
                    </Button>
                    <Button
                      appearance="link"
                      size="sm"
                      onClick={() => navigate(`/drivers/${rowData.userUID}/edit`)}
                    >
                      查看司機
                    </Button>
                  </>
                )}
              </Table.Cell>
            </Table.Column>
          </Table>
        ) : (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Placeholder.Paragraph rows={3} />
            <p style={{ color: "#999" }}>目前沒有待核准的代理隊長申請</p>
          </div>
        )}
      </Panel>
    </AdminDashboard>
  );
}
